import { create } from 'zustand';
import type { MixerState } from '@shared/types';
import { VIDEO_DUCKING_DEFAULT_STRENGTH } from '@shared/constants';

/** Track-index type for the SFX/Music bed channels. Up to 2 tracks. */
export type SfxTrackIdx = 0 | 1;

interface MixerSlice extends MixerState {
  setVoiceVolume(v: number): void;
  setVoiceMuted(m: boolean): void;
  setVideoUserVolume(v: number): void;
  setVideoMuted(m: boolean): void;
  setSfxVolume(track: SfxTrackIdx, v: number): void;
  setSfxMuted(track: SfxTrackIdx, m: boolean): void;
  setDuckingEnabled(enabled: boolean): void;
  setDuckingStrength(strength: number): void;
}

export const useMixerStore = create<MixerSlice>((set) => ({
  voiceVolume: 1,
  voiceMuted: false,
  videoUserVolume: 0.5,
  videoMuted: false,
  // Both bed faders default to 50% — matches the Video channel default so a
  // freshly loaded bed sits at a comfortable level under the voice rather
  // than blasting at unity gain. Each track's slider drives its own gain.
  sfxVolumes: [0.5, 0.5],
  sfxMuted: [false, false],
  videoDuckingEnabled: true,
  videoDuckingStrength: VIDEO_DUCKING_DEFAULT_STRENGTH,

  setVoiceVolume: (voiceVolume) => set({ voiceVolume }),
  setVoiceMuted: (voiceMuted) => set({ voiceMuted }),
  setVideoUserVolume: (videoUserVolume) => set({ videoUserVolume }),
  setVideoMuted: (videoMuted) => set({ videoMuted }),
  setSfxVolume: (track, v) =>
    set((s) => {
      const next: [number, number] = [s.sfxVolumes[0], s.sfxVolumes[1]];
      next[track] = v;
      return { sfxVolumes: next };
    }),
  setSfxMuted: (track, m) =>
    set((s) => {
      const next: [boolean, boolean] = [s.sfxMuted[0], s.sfxMuted[1]];
      next[track] = m;
      return { sfxMuted: next };
    }),
  setDuckingEnabled: (videoDuckingEnabled) => set({ videoDuckingEnabled }),
  setDuckingStrength: (videoDuckingStrength) => set({ videoDuckingStrength }),
}));

/** Effective voice gain (0..1) accounting for mute + slider. */
export function effectiveVoiceVolume(s: MixerState): number {
  return s.voiceMuted ? 0 : s.voiceVolume;
}

/** Effective video gain considering mute + user slider + optional ducked flag. */
export function effectiveVideoVolume(s: MixerState, ducked: boolean): number {
  if (s.videoMuted) return 0;
  const base = s.videoUserVolume;
  if (!ducked || !s.videoDuckingEnabled) return base;
  const reduction = s.videoDuckingStrength / 100;
  return base * (1 - reduction);
}

/** Effective SFX/music bed gain for the given track (0 or 1). Ducking
 *  strength is shared across all bed tracks AND the video track — a single
 *  slider drives every duckable target. */
export function effectiveSfxVolume(
  s: MixerState,
  ducked: boolean,
  track: SfxTrackIdx,
): number {
  if (s.sfxMuted[track]) return 0;
  const base = s.sfxVolumes[track];
  if (!ducked || !s.videoDuckingEnabled) return base;
  const reduction = s.videoDuckingStrength / 100;
  return base * (1 - reduction);
}
